// covered-by: frontends/carrie/src/ask-v2/home/__tests__/useAskV2MentionTasks.test.ts
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  type AskV2MentionTask,
  enrichTaskFromChatLookup,
  mentionTasksFromNotifications,
} from "./mentionTasks";

import * as AskPunttService from "../../services/askPuntt";
import {
  ASK_PUNTT_UNREAD_LIST_POLL_MS,
  askPunttKeys,
  useAskPunttChats,
} from "../../utils/queries/askPuntt";
import { useCurrentUserProfile } from "../../utils/queries/userProfile";

export type AskV2MentionTasksState = {
  tasks: AskV2MentionTask[];
  pending: boolean;
};

function mentionTaskNeedsChatKindFetch(task: AskV2MentionTask): boolean {
  return Boolean(task.projectId?.trim() && task.projectChatKind == null);
}

/**
 * Unread @mention tasks for the home page, read off the profile's stored
 * notifications. Polls at the sidebar's unread cadence so a mention posted
 * while the reader sits on the home lands within one poll.
 */
export function useAskV2MentionTasks(): AskV2MentionTasksState {
  const pollInterval = ASK_PUNTT_UNREAD_LIST_POLL_MS;
  const profile = useCurrentUserProfile({
    refetchInterval: pollInterval,
  });
  const { data: chatsData } = useAskPunttChats({
    scope: "all",
    refetchInterval: pollInterval,
  });
  const notifications = profile.data?.notifications;
  const chatById = useMemo(() => {
    const chats = chatsData?.chats;
    if (!chats?.length) return undefined;
    return new Map(
      chats.map((chat) => [
        chat._id,
        {
          projectId: chat.projectId,
          projectChatKind: chat.projectChatKind,
        },
      ]),
    );
  }, [chatsData?.chats]);
  const listEnrichedTasks = useMemo(
    () => mentionTasksFromNotifications(notifications, chatById),
    [notifications, chatById],
  );
  const chatIdsNeedingKind = useMemo(() => {
    const ids = new Set<string>();
    for (const task of listEnrichedTasks) {
      if (mentionTaskNeedsChatKindFetch(task)) {
        ids.add(task.chatId);
      }
    }
    return Array.from(ids);
  }, [listEnrichedTasks]);
  // why: list scopes exclude the project's shared feed chat, so project-home
  // mentions must load kind from GET /chats/:id (same payload as history).
  const kindQueries = useQueries({
    queries: chatIdsNeedingKind.map((chatId) => ({
      queryKey: [...askPunttKeys.chat(chatId), "mention-task-kind"] as const,
      queryFn: async () => {
        const { chat } = await AskPunttService.getChatHistory(chatId);
        return {
          projectId: chat.projectId ?? null,
          projectChatKind: chat.projectChatKind ?? null,
        };
      },
      staleTime: pollInterval,
    })),
  });
  const tasks = useMemo(() => {
    if (chatIdsNeedingKind.length === 0) {
      return listEnrichedTasks;
    }
    const fetchedByChatId = new Map<
      string,
      {
        projectId: string | null;
        projectChatKind: "shared" | "personal" | null;
      }
    >();
    chatIdsNeedingKind.forEach((chatId, index) => {
      const lookup = kindQueries[index]?.data;
      if (lookup) {
        fetchedByChatId.set(chatId, lookup);
      }
    });
    if (fetchedByChatId.size === 0) {
      return listEnrichedTasks;
    }
    return listEnrichedTasks.map((task) =>
      enrichTaskFromChatLookup(task, fetchedByChatId.get(task.chatId)),
    );
  }, [chatIdsNeedingKind, kindQueries, listEnrichedTasks]);
  return { tasks, pending: profile.isPending };
}
